import React from 'react'

import { LoadingPlaceholder } from '@sketch/components'
import { ReactComponent as DocumentIcon } from '@sketch/icons/document-17'
import { thumbnailForPreviewFile } from '@sketch/modules-common'
import { isTruthy } from '@sketch/utils'

import { FolderArchivedNotificationDetailsFragment } from '@sketch/gql-types'

import {
  FolderGrid,
  FolderTile,
  FolderTileIcon,
  FolderTileImage,
} from './FolderNotificationPreview.styles'

interface FolderTileMediaProps {
  folder: FolderArchivedNotificationDetailsFragment['folder']
}

const FOLDER_TILE_COUNT = 4

/**
 * Folder-contents tile for a preview card: a 2×2 grid of the folder's document
 * thumbnails. Mirrors the list-item `FolderPreview` so the inbox list and the
 * preview panel surface a folder the same way. Falls back to empty tiles when
 * the folder has no previews.
 */
export const FolderTileMedia = ({ folder }: FolderTileMediaProps) => {
  const previews = folder?.previews.slice(0, FOLDER_TILE_COUNT) ?? []

  if (previews.length === 0) {
    return (
      <FolderGrid aria-hidden>
        {Array.from({ length: FOLDER_TILE_COUNT }).map((_, index) => (
          <FolderTile key={index} />
        ))}
      </FolderGrid>
    )
  }

  return (
    <FolderGrid aria-label={`Folder ${folder?.name ?? ''}`}>
      {previews.map(({ name, file }, index) => {
        const thumbnails = (file?.thumbnails || []).filter(isTruthy)

        return (
          <FolderTile key={index}>
            {thumbnails.length === 0 ? (
              <FolderTileIcon>
                <DocumentIcon aria-label="No image available" />
              </FolderTileIcon>
            ) : (
              <FolderTileImage
                src={thumbnailForPreviewFile(file)}
                alt={name}
                loadingPlaceholder={<LoadingPlaceholder />}
              />
            )}
          </FolderTile>
        )
      })}
    </FolderGrid>
  )
}
